import { useLocalStorage } from '@festina-life/ui/hooks'
import { type ReactElement, useCallback, useMemo, useState } from 'react'

import { useFetchAppSettings } from '../hooks/useFetchAppSettings'
import {
  type EnvironmentSettingsWithApi,
  getEnvironmentSettings,
} from '../utility/getEnvironmentSettings'
import {
  type AuthenticationContextType,
  Provider,
  type PseudonymizationSettings,
} from './authenticationContext'

type Props = {
  children:
    | ReactElement
    | ((
        currentContext: AuthenticationContextType,
      ) => ReactElement | null | false)
}

/** Context Provider for the Life portal settings.  */
export const AuthenticationProvider = ({ children }: Props) => {
  const { data: appSettings } = useFetchAppSettings()

  const [environmentSettings, api] = useMemo(
    () => (appSettings ? getEnvironmentSettings(appSettings) : [null, null]),
    [appSettings],
  )

  const [shouldShowUserInfo, setShouldShowUserInfo] = useState(false)

  const [validateAuthenticationHooks, setValidateAuthenticationHooks] =
    useLocalStorage<boolean>('shouldValidateAuthenticationHooks')

  const isInTestMode = useMemo(
    () => !!environmentSettings?.REACT_APP_TEST_MODE,
    [environmentSettings],
  )

  const isUsingExternalCrm = useMemo(
    () => !!environmentSettings?.REACT_APP_USE_EXTERNAL_CRM,
    [environmentSettings],
  )

  const isAiAgentEnabled = useMemo(
    () =>
      isInTestMode && !!environmentSettings?.['REACT_APP_AGENT_API_ENDPOINT'],
    [environmentSettings, isInTestMode],
  )

  const pseudonymization = useMemo(
    () =>
      ({
        moneyOut: {
          paymentChannel:
            !!environmentSettings?.REACT_APP_PSEUDONYMIZATION_MONEYOUT_PAYMENTCHANNEL,
          payableStop:
            !!environmentSettings?.REACT_APP_PSEUDONYMIZATION_MONEYOUT_PAYABLESTOP,
        },
      }) satisfies PseudonymizationSettings,
    [environmentSettings],
  )

  const shouldValidateAuthenticationHooks = useMemo(() => {
    if (isInTestMode) {
      return validateAuthenticationHooks ?? false
    } else {
      return true
    }
  }, [isInTestMode, validateAuthenticationHooks])

  const handleChangeEnvironmentSettings = useCallback(
    (nextSettings: EnvironmentSettingsWithApi) => {
      const { api } = nextSettings

      if (api === 'customize') {
        localStorage.setItem('appSettings', JSON.stringify(nextSettings))
      } else {
        localStorage.setItem('appSettings', JSON.stringify({ api }))
      }

      window.location.reload()
    },
    [],
  )

  const value = useMemo(
    () =>
      ({
        api,
        environmentSettings,
        onChangeEnvironmentSettings: handleChangeEnvironmentSettings,
        onChangeShouldValidateAuthenticationHooks:
          setValidateAuthenticationHooks,
        shouldValidateAuthenticationHooks,
        isInTestMode,
        isUsingExternalCrm,
        isAiAgentEnabled,
        shouldShowUserInfo,
        pseudonymization,
        setShouldShowUserInfo,
      }) satisfies AuthenticationContextType,
    [
      api,
      environmentSettings,
      handleChangeEnvironmentSettings,
      setValidateAuthenticationHooks,
      shouldValidateAuthenticationHooks,
      isInTestMode,
      isUsingExternalCrm,
      isAiAgentEnabled,
      shouldShowUserInfo,
      pseudonymization,
    ],
  )

  if (!environmentSettings) {
    return null
  }

  return (
    <Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </Provider>
  )
}
